import axios from 'axios';

import { config } from './config';
import { GetNet } from './providers/GetNet';
import { Iugu } from './providers/Iugu';
import { PagarMeV5 } from './providers/PagarMeV5';
import { Z2Pay } from './providers/Z2Pay';
import { CardConfig, IProvider } from './types';

type IOptions = {
  key: string;
  card: CardConfig;
};

type ProviderType = 'pagarme' | 'iugu' | 'z2pay';

type ProviderConfig = {
  gatewayConfigId: string;
  provider: ProviderType;
  isTest: boolean;
  config: any;
};

const providerMap = {
  pagarme: (options: any, gatewayConfigId: string, isTest?: boolean) => new PagarMeV5(options, gatewayConfigId, isTest),
  iugu: (options: any, gatewayConfigId: string, isTest?: boolean) => new Iugu(options, gatewayConfigId, isTest),
  getnet: (options: any, gatewayConfigId: string, isTest?: boolean) => new GetNet(options, gatewayConfigId, isTest),
  z2pay: (options: any, gatewayConfigId: string, isTest?: boolean) => new Z2Pay(options, gatewayConfigId, isTest),
} as {
  [key: string]: (options: any, gatewayConfigId: string, isTest?: boolean) => IProvider;
};

class RouterFy {
  public async generateToken(options: IOptions) {
    const appData = await this.loadTokenShape(options.key);
    const promises = appData
      .map(async (app) => {
        const provider = providerMap[app.provider]!(app.config, app.gatewayConfigId, app.isTest);
        if (!provider) throw new Error('Provider not found');

        const token = await provider.generate(options.card);
        return {
          gatewayConfigId: app.gatewayConfigId,
          provider: app.provider,
          token,
        };
      });

    try {
      const result = await Promise.race([
        Promise.allSettled(promises),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 20_000)
        ),
      ]) as any;

      const tokens = result
        .filter((r: any) => r.status === 'fulfilled')
        .map(
          (r: any) =>
            (r as any).value as {
              provider: ProviderType;
              token: string;
              gatewayConfigId: string;
            }
        );

      if (!tokens.length) {
        throw new Error('Error to generate token');
      }

      const [expirationMonth, expirationYear] = options?.card?.expiration.split('/');

      const payload = {
        tokens,
        info: {
          firstDigits: options?.card?.number?.slice(0, 6),
          lastDigits: options?.card?.number?.slice(-4),
          holderName: options?.card?.holder,
          expirationMonth,
          expirationYear,
        }
      }

      return btoa(JSON.stringify(payload));
    } catch (err) {
      console.log('Error generating token', err);
      throw err;
    }
  }

  private async loadTokenShape(key: string): Promise<ProviderConfig[]> {
    try {
      const response = await axios.get<ProviderConfig[]>(`${config.API_ENDPOINT}/config/${key}`);
      return response.data;
    } catch (err: any) {
      console.log('Error loading token shape', err?.response?.data);
      // fallback?
      throw err;
    }
  }
}

export const routerFy = new RouterFy();
